import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMegaphone, FiCalendar, FiEye, FiDollarSign, FiStar, FiTrendingUp, FiShield } = FiIcons;

const Dashboard = () => {
  const kpiData = [
    { label: 'Views This Week', value: '342', change: '+12%', icon: FiEye, color: 'trust' },
    { label: 'Bookings This Month', value: '28', change: '+8%', icon: FiCalendar, color: 'olive' },
    { label: 'Income This Month', value: '$3,240', change: '+15%', icon: FiDollarSign, color: 'sand' },
    { label: 'New Reviews', value: '12', change: '+4', icon: FiStar, color: 'trust' },
  ];

  const topService = {
    name: 'Home Cleaning',
    bookings: 18,
    revenue: '$1,890',
    rating: 4.9
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Welcome Card */}
      <motion.div 
        className="bg-gradient-to-r from-trust-500 to-trust-600 rounded-2xl p-8 text-white"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Welcome back, Amina!</h1>
            <p className="text-trust-100 text-lg">
              Your profile has a 94% trust rating — clients love your punctuality.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-full p-4">
              <SafeIcon icon={FiShield} className="text-3xl" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">94%</div>
              <div className="text-trust-100">Trust Score</div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* KPIs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiData.map((kpi, index) => (
          <motion.div
            key={kpi.label}
            className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-lg bg-${kpi.color}-100`}>
                <SafeIcon icon={kpi.icon} className={`text-xl text-${kpi.color}-600`} />
              </div>
              <span className="text-green-600 text-sm font-medium">{kpi.change}</span>
            </div>
            <div className="text-2xl font-bold text-slate-800 mb-1">{kpi.value}</div>
            <div className="text-slate-600 text-sm">{kpi.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Top Performing Service */}
      <motion.div 
        className="bg-white rounded-xl p-6 shadow-sm border border-slate-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        <h2 className="text-xl font-bold text-slate-800 mb-4">Top-Performing Service</h2>
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-olive-50 to-sand-50 rounded-lg">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-olive-400 to-sand-400 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiStar} className="text-white text-2xl" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">{topService.name}</h3>
              <div className="flex items-center gap-4 text-sm text-slate-600">
                <span>{topService.bookings} bookings</span>
                <span>{topService.revenue} revenue</span>
                <div className="flex items-center gap-1">
                  <SafeIcon icon={FiStar} className="text-yellow-500" />
                  <span>{topService.rating}</span>
                </div>
              </div>
            </div>
          </div>
          <SafeIcon icon={FiTrendingUp} className="text-2xl text-olive-600" />
        </div>
      </motion.div>

      {/* Action Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.button
          className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:shadow-md transition-all duration-200 hover:border-trust-300"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="p-3 bg-trust-100 rounded-lg">
            <SafeIcon icon={FiPhone} className="text-trust-600 text-xl" />
          </div>
          <div className="text-left">
            <div className="font-medium text-slate-800">View My Number</div>
            <div className="text-sm text-slate-500">Manage contact info</div>
          </div>
        </motion.button>

        <motion.button
          className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:shadow-md transition-all duration-200 hover:border-olive-300"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="p-3 bg-olive-100 rounded-lg">
            <SafeIcon icon={FiMegaphone} className="text-olive-600 text-xl" />
          </div>
          <div className="text-left">
            <div className="font-medium text-slate-800">Promote My Services</div>
            <div className="text-sm text-slate-500">Boost visibility</div>
          </div>
        </motion.button>

        <motion.button
          className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:shadow-md transition-all duration-200 hover:border-sand-300"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="p-3 bg-sand-100 rounded-lg">
            <SafeIcon icon={FiCalendar} className="text-sand-600 text-xl" />
          </div>
          <div className="text-left">
            <div className="font-medium text-slate-800">Update My Availability</div>
            <div className="text-sm text-slate-500">Set schedule</div>
          </div>
        </motion.button>
      </div>
    </div>
  );
};

export default Dashboard;